
export interface CivicEducationBase {
  id: string;
  title: string;
  description: string;
  date: string;
  tags: string[];
  related_issues: string[];
  country_code?: string | null;
  status: 'draft' | 'published' | 'archived';
  created_at?: string;
  updated_at?: string;
  type: 'podcast' | 'video' | 'summary';
}

export interface Podcast extends CivicEducationBase {
  type: 'podcast';
  episode_id: string;
  duration?: number;
  host?: string;
  source?: string;
}

export interface Video extends CivicEducationBase {
  type: 'video';
  video_id: string;
  duration?: number;
  source?: string;
  thumbnail_url?: string;
}

export interface Summary extends CivicEducationBase {
  type: 'summary';
  document_url: string;
  source: string;
  content: string;
  audio_summary?: boolean;
}

export type CivicEducationItem = Podcast | Video | Summary;

// Transform functions for database operations
export const transformDatabaseContent = (dbItem: any): CivicEducationItem => {
  const baseContent = {
    id: dbItem.id,
    title: dbItem.title,
    description: dbItem.description || '',
    date: dbItem.date || new Date(dbItem.created_at).toISOString().split('T')[0],
    tags: dbItem.tags || [],
    related_issues: dbItem.related_issues || [],
    country_code: dbItem.country_code,
    status: dbItem.status || 'draft',
    created_at: dbItem.created_at,
    updated_at: dbItem.updated_at,
    type: dbItem.content_type
  };

  switch (dbItem.content_type) {
    case 'podcast':
      return {
        ...baseContent,
        type: 'podcast',
        episode_id: dbItem.content_details?.episode_id || '',
        duration: dbItem.content_details?.duration,
        host: dbItem.content_details?.host,
        source: dbItem.content_details?.source || 'spotify'
      } as Podcast;
    case 'video':
      return {
        ...baseContent,
        type: 'video',
        video_id: dbItem.content_details?.video_id || '',
        duration: dbItem.content_details?.duration,
        source: dbItem.content_details?.source || 'youtube',
        thumbnail_url: dbItem.content_details?.thumbnail_url
      } as Video;
    case 'summary':
      return {
        ...baseContent,
        type: 'summary',
        document_url: dbItem.content_details?.document_url || '',
        source: dbItem.content_details?.source || '',
        content: dbItem.content_details?.content || '',
        audio_summary: dbItem.content_details?.audio_summary
      } as Summary;
    default:
      throw new Error(`Unknown content type: ${dbItem.content_type}`);
  }
};

export const transformContentForDatabase = (item: CivicEducationItem) => {
  const baseItem = {
    id: item.id,
    title: item.title,
    description: item.description,
    date: item.date,
    tags: item.tags,
    related_issues: item.related_issues,
    country_code: item.country_code || null,
    status: item.status || 'draft',
    content_type: item.type
  };

  let contentDetails = {};

  switch (item.type) {
    case 'podcast':
      contentDetails = {
        episode_id: (item as Podcast).episode_id,
        duration: (item as Podcast).duration,
        host: (item as Podcast).host,
        source: (item as Podcast).source || 'spotify'
      };
      break;
    case 'video':
      contentDetails = {
        video_id: (item as Video).video_id,
        duration: (item as Video).duration,
        source: (item as Video).source || 'youtube',
        thumbnail_url: (item as Video).thumbnail_url
      };
      break;
    case 'summary':
      contentDetails = {
        document_url: (item as Summary).document_url,
        source: (item as Summary).source,
        content: (item as Summary).content,
        audio_summary: (item as Summary).audio_summary
      };
      break;
  }

  return {
    ...baseItem,
    content_details: contentDetails
  };
};
